// Other plugins.
import { logManager } from '../../logs'

const log = logManager.getLogger('SDPHANDLER')

/**
 * SDP handler function that should be passed into session object's `processOffer` and `processAnswer` function.
 * This function disables old media encryption method SDES (Modifies sdp by removing crypto).
 * However, if only SDES is available, don't disable it.
 *
 * @method sanitizeSdesFromSdp
 * @param {Object} newSdp The SDP so far (could have been modified by previous handlers).
 * @param {Object} info Information about the session description.
 * @param {RTCSdpType} info.type The session description's type.
 * @param {string} info.step The step that will occur after the Pipeline is run.
 * @param {string} info.endpoint Which end of the connection created the SDP.
 * @param {Object} originalSdp The SDP in its initial state.
 * @return {Object} The sanitized SDP with crypto removed (if fingerprint exists)
 */
export function sanitizeSdesFromSdp (newSdp, info, originalSdp) {
  for (const mLine of newSdp.media) {
    if (mLine.crypto && mLine.fingerprint) {
      log.debug('Removing SDES line from SDP media section.')
      delete mLine.crypto
    }
  }
  return newSdp
}

/**
 * Function generator for an SDP handler function that changes the DTLS role of the SDP.
 * @method changeDtlsRoleTo
 * @param  {string} role
 * @return {Function} SDP handler.
 */
export function changeDtlsRoleTo (role) {
  return function changeDtlsRole (newSdp, info, originalSdp) {
    // Grab the original DTLS role for logging purposes.
    //    Assumed that the DTLS role is the same in every media section.
    const original = newSdp.media[0].setup
    log.debug(`Changing SDP DTLS role from ${original} to ${role}.`)

    // Change the DTLS role in every media section.
    newSdp.media.map(media => {
      media.setup = role
      return media
    })

    return newSdp
  }
}

/**
 * An SDP handler that adds bandwidth options to the SDP.
 * Supports Chrome & Firefox by generating 2 types of configs:
 *  - 'AS' for Chrome in kilobits per second
 *  - 'TIAS' for Firefox in bits per second
 * @method modifySdpBandwidth
 * @param {BandwidthControls} bandwidthControls
 * @return {Function} SDP handler.
 */
export function modifySdpBandwidth (newSdp, info, originalSdp) {
  /**
   * Generates bandwidth configs that Chrome & Firefox can recognize.
   * @param {number} limit The bandwidth limit in kilobits per second.
   */
  function generateBandwidthConfigs (limit) {
    return [
      {
        type: 'AS', // Chrome supports this (in kilobits per second)
        limit
      },
      {
        type: 'TIAS', // Firefox supports this (in bits per second)
        limit: limit * 1000
      }
    ]
  }

  if (info.bandwidth) {
    // For more details on bandwidth controls, see here https://webrtchacks.com/limit-webrtc-bandwidth-sdp/.

    if (info.bandwidth.call) {
      // Set session level bandwidth limit
      newSdp.bandwidth = generateBandwidthConfigs(info.bandwidth.call)
    }

    if (info.bandwidth.audio) {
      // Set audio bandwidth limit
      newSdp.media
        .filter(media => media.type === 'audio')
        .forEach(media => {
          media.bandwidth = generateBandwidthConfigs(info.bandwidth.audio)
        })
    }

    if (info.bandwidth.video) {
      // Set video bandwidth limit
      newSdp.media
        .filter(media => media.type === 'video')
        .forEach(media => {
          media.bandwidth = generateBandwidthConfigs(info.bandwidth.video)
        })
    }
  }
  return newSdp
}

/**
 * Generates extra informational parameters for ending a call.
 *
 * @method generateEndParams
 * @param {string}  currentCallState The state of the current call, before it was ended.
 * @param {boolean} isLocal Specifies if end operation was caused by the local side.
 * @param {Object}  params Extra context information related to the call.
 * @param {string}  [params.reasonText]  Human-readable explanation for the call change.
 * @param {string}  [params.statusCode] Code representing the reason for the call change.
 * @param {string}  params.remoteName   Name of the remote participant.
 * @param {string}  params.remoteNumber Number of the remote participant.
 */
export function generateEndParams (currentCallState, isLocal, params) {
  const endParams = {
    isLocal,
    remoteParticipant: {
      displayNumber: params.remoteNumber,
      displayName: params.remoteName
    },
    transition: {
      prevState: currentCallState
    }
  }
  if (params.statusCode) {
    endParams.transition.statusCode = params.statusCode
  }
  if (params.reasonText) {
    endParams.transition.reasonText = params.reasonText
  }
  return endParams
}
